import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { Button } from "@faire/web--source/slate/Button";
import { Calendar } from "@faire/web--source/slate/icons/foundation/Calendar";
import { Information } from "@faire/web--source/slate/icons/foundation/Information";
import { Shipping } from "@faire/web--source/slate/icons/foundation/Shipping";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { useQuery as useBrandViewQuery } from "@faire/web-api--source/api/v2/brand-view/brandToken/get-hooks";
import { IInformationMessage } from "@faire/web-api--source/indigofair/data/IInformationMessage";
import React, { useMemo } from "react";
import styled, { css } from "styled-components";
import { TextWithTooltip } from "@faire/retailer/components/TextWithTooltip";
import { ExclusivityDetailsLink } from "@faire/retailer/features/Exclusivity/DetailsModal/ExclusivityDetailsLink";
import { useExclusivityAvailabilityWithState } from "@faire/retailer/hooks/exclusivity/useExclusivityAvailabilityWithState";
import { isLoggedInRetailerOrBrandPreview } from "@faire/retailer/lib/isLoggedInRetailer";
import { getSettingInternationalBuyingNudges } from "@faire/retailer/settings/getSettingInternationalBuyingNudges";
import { useSettingOwFImproveTermsTouchpointsPDPShipping } from "@faire/retailer/settings/useSettingOwFImproveTermsTouchpointsPDPShipping";
import { buildFulfillmentTime } from "@faire/retailer/views/brand/utils/leadTimeUtils";
export var ShippingDetailsList = function(param) {
    var brand = param.brand, infoTexts = param.infoTexts, importShipmentInfo = param.importShipmentInfo, shippingPromotion = param.shippingPromotion, isPreorder = param.isPreorder;
    var shipsFrom = [];
    var strictLocalize = useStrictLocalization().strictLocalize;
    var isMobile = useViewport().isMobile;
    var settingOwFImproveTermsTouchpointsPDPShipping = useSettingOwFImproveTermsTouchpointsPDPShipping();
    var _brand_token;
    var _useBrandViewQuery = useBrandViewQuery((_brand_token = brand.token) !== null && _brand_token !== void 0 ? _brand_token : "", undefined, {
        enabled: !!brand.token
    }), data = _useBrandViewQuery.data, isPending = _useBrandViewQuery.isPending;
    var estimatedDeliveryMessage = useMemo(function() {
        var _data_info_texts;
        return data === null || data === void 0 ? void 0 : (_data_info_texts = data.info_texts) === null || _data_info_texts === void 0 ? void 0 : _data_info_texts.find(function(infoText) {
            return infoText.type === IInformationMessage.Type.GET_IT_BY && !isPreorder;
        });
    }, [
        data,
        isPreorder
    ]);
    if ((estimatedDeliveryMessage === null || estimatedDeliveryMessage === void 0 ? void 0 : estimatedDeliveryMessage.type) && (estimatedDeliveryMessage === null || estimatedDeliveryMessage === void 0 ? void 0 : estimatedDeliveryMessage.text) && (estimatedDeliveryMessage === null || estimatedDeliveryMessage === void 0 ? void 0 : estimatedDeliveryMessage.tooltip)) {
        shipsFrom.push({
            id: estimatedDeliveryMessage.type,
            icon: /*#__PURE__*/ _jsx(Calendar, {}),
            text: /*#__PURE__*/ _jsx(TextWithTooltip, {
                text: estimatedDeliveryMessage.text,
                tooltipText: estimatedDeliveryMessage.tooltip,
                tooltipWidth: isMobile ? 200 : undefined
            })
        });
    }
    var hasLeadTimeAsRange = brand.upper_bound_lead_time_days !== brand.lower_bound_lead_time_days && isNotUndefined(brand.lower_bound_lead_time_days) && isNotUndefined(brand.upper_bound_lead_time_days);
    if (!isLoggedInRetailerOrBrandPreview() && hasLeadTimeAsRange) {
        shipsFrom.push({
            id: "FULFILLMENT_TIME_CUSTOM",
            icon: /*#__PURE__*/ _jsx(Calendar, {}),
            text: /*#__PURE__*/ _jsx(Typography, {
                children: buildFulfillmentTime({
                    leadTimeDays: brand.lead_time_days,
                    upperBoundLeadTimeDays: brand.upper_bound_lead_time_days,
                    lowerBoundLeadTimeDays: brand.lower_bound_lead_time_days
                }, strictLocalize)
            })
        });
    }
    if (infoTexts) {
        infoTexts.map(function(infoText, index) {
            var _infoText_type;
            shipsFrom.push({
                id: (_infoText_type = infoText.type) !== null && _infoText_type !== void 0 ? _infoText_type : "UNKNOWN_".concat(index),
                icon: infoText.icon_url ? /*#__PURE__*/ _jsx(IconImg, {
                    src: infoText.icon_url,
                    $iconSize: getIconImgSize(infoText.type)
                }) : null,
                text: /*#__PURE__*/ _jsxs(Typography, {
                    children: [
                        infoText.text,
                        " ",
                        /*#__PURE__*/ _jsx(CustomLink, {
                            linkUrl: infoText.help_link,
                            brandToken: brand.token,
                            type: infoText.type,
                            ctaText: infoText.cta_text
                        })
                    ]
                })
            });
        });
    } else if (importShipmentInfo && getSettingInternationalBuyingNudges()) {
        shipsFrom.push({
            id: "IMPORT_SHIPMENT_INFO",
            icon: null,
            text: /*#__PURE__*/ _jsx(Typography, {
                children: importShipmentInfo
            })
        });
    }
    if ((shippingPromotion === null || shippingPromotion === void 0 ? void 0 : shippingPromotion.type) && (shippingPromotion === null || shippingPromotion === void 0 ? void 0 : shippingPromotion.text) && (shippingPromotion === null || shippingPromotion === void 0 ? void 0 : shippingPromotion.tooltip)) {
        shipsFrom.push({
            id: shippingPromotion.type,
            icon: /*#__PURE__*/ _jsx(Shipping, {
                width: "21px",
                height: "21px"
            }),
            text: /*#__PURE__*/ _jsx(TextWithTooltip, {
                text: shippingPromotion.text,
                tooltipText: shippingPromotion.tooltip,
                tooltipWidth: isMobile ? 200 : undefined
            })
        });
    }
    if (shipsFrom.length <= 0 || isPending) {
        return null;
    }
    if (settingOwFImproveTermsTouchpointsPDPShipping) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(Spacer, {
                    size: "3x"
                }),
                /*#__PURE__*/ _jsxs(Column, {
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraphSansMedium",
                            children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                id: "9QKfPT",
                                defaultMessage: [
                                    {
                                        type: 0,
                                        value: "Shipping & policies"
                                    }
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(Spacer, {
                            size: 12
                        }),
                        /*#__PURE__*/ _jsx("ul", {
                            children: shipsFrom.map(function(item) {
                                var _item_icon;
                                return /*#__PURE__*/ _jsx(ListItemNoBullet, {
                                    children: /*#__PURE__*/ _jsxs(Row, {
                                        gap: "1x",
                                        children: [
                                            /*#__PURE__*/ _jsx(IconWrapper, {
                                                children: (_item_icon = item.icon) !== null && _item_icon !== void 0 ? _item_icon : /*#__PURE__*/ _jsx(Information, {})
                                            }),
                                            item.text
                                        ]
                                    })
                                }, "shipping-info-".concat(item.id));
                            })
                        })
                    ]
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Spacer, {
                size: "3x"
            }),
            /*#__PURE__*/ _jsxs(Column, {
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraphSansMedium",
                        children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                            id: "qsU7kw",
                            defaultMessage: [
                                {
                                    type: 0,
                                    value: "Shipping"
                                }
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        size: "1x"
                    }),
                    /*#__PURE__*/ _jsx("ul", {
                        children: shipsFrom.map(function(item) {
                            return /*#__PURE__*/ _jsx(ListItem, {
                                children: item.text
                            }, "shipping-info-".concat(item.id));
                        })
                    })
                ]
            })
        ]
    });
};
var CustomLink = function(param) {
    var linkUrl = param.linkUrl, type = param.type, ctaText = param.ctaText, brandToken = param.brandToken;
    var availability = useExclusivityAvailabilityWithState(brandToken).availability;
    var settingOwFImproveTermsTouchpointsPDPShipping = useSettingOwFImproveTermsTouchpointsPDPShipping();
    if (!settingOwFImproveTermsTouchpointsPDPShipping || !ctaText) {
        return null;
    }
    if (type === IInformationMessage.Type.EXCLUSIVITY_ACTIVE && (availability === null || availability === void 0 ? void 0 : availability.exclusivity_agreement_token)) {
        return /*#__PURE__*/ _jsx(ExclusivityDetailsLink, {
            agreementToken: availability === null || availability === void 0 ? void 0 : availability.exclusivity_agreement_token,
            brandToken: brandToken,
            origin: "product",
            color: "inherit",
            children: ctaText
        });
    }
    if (type === IInformationMessage.Type.EXCLUSIVITY_AVAILABLE && linkUrl) {
        return /*#__PURE__*/ _jsx(Button, {
            variant: "tertiary",
            linkTo: linkUrl,
            tertiaryColor: "inherit",
            children: ctaText
        });
    }
    return null;
};
var getIconImgSize = function(messageType) {
    switch(messageType){
        case IInformationMessage.Type.EXCLUSIVITY_ACTIVE:
        case IInformationMessage.Type.EXCLUSIVITY_AVAILABLE:
            return 22;
        case IInformationMessage.Type.SHIPS_FROM:
            return 18;
        default:
            return 20;
    }
};
var IconWrapper = styled.div.withConfig({
    componentId: "sc-8f8bbe5e-0"
})([
    "display:flex;align-items:center;justify-content:center;height:20px;width:20px;flex-shrink:0;"
]);
var IconImg = styled.img.withConfig({
    componentId: "sc-8f8bbe5e-1"
})([
    "",
    ";"
], function(param) {
    var $iconSize = param.$iconSize;
    return css([
        "height:",
        "px;width:",
        "px;"
    ], $iconSize, $iconSize);
});
var ListItem = styled.li.withConfig({
    componentId: "sc-8f8bbe5e-2"
})([
    "list-style-type:disc;margin-left:",
    ";"
], getSpacing("2x"));
var ListItemNoBullet = styled.li.withConfig({
    componentId: "sc-8f8bbe5e-3"
})([
    "list-style-type:none;padding:0;padding-bottom:12px;margin:0;"
]);
